import type { HumanoidAppearance } from '../GhostRenderer';
import { HUSH_RENDER_TOKENS } from '../../../shared/assets/hushStatic';
import { adaptWardrobeAppearance } from '../hushWardrobe/castAdapter';
import { HUSH_WARDROBE_MALLOW, HUSH_WARDROBE_PIP, HUSH_WARDROBE_RESOURCES } from '../hushWardrobe/recipes';
import { npc } from './npc';

/**
 * Hush Nook cast bodies and faces: Pip Reedwhistle and Mallow Wick, before the wardrobe.
 * Exported so tests can compare the adopted look's body and face against these sources.
 */
export const HUSH_NOOK_BASE_CAST = {
  pip_reedwhistle: npc({
    shape: 'slim',
    color: '#b0a390',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      fit: true,
      hair: { style: 'short', color: '#6a523a' },
      garment: { kind: 'tunic', color: '#927754', trimColor: '#6c9bae', beltColor: '#594c3b' },
      props: [
        { kind: 'scarf', color: '#6c9bae', stripeColor: '#aac8ce' },
        { kind: 'satchel', color: '#826b4d' },
      ],
      art: { light: 'overcast', role: 'local' },
    },
  }),
  mallow_wick: npc({
    shape: 'round',
    color: '#a0a89d',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      fit: true,
      hair: { style: 'bun', color: '#747c72', streakColor: '#b5bda7' },
      cloak: { color: '#78966b', claspColor: '#ffe3a0' },
      garment: { kind: 'robe', color: '#4a5b54', trimColor: '#8c9c96', beltColor: '#2b4145' },
      props: [
        { kind: 'candle', color: '#826b4d', flameColor: '#ffe3a0' },
      ],
      art: { light: 'lamp', role: 'keeper' },
    },
  }),
} satisfies Record<string, HumanoidAppearance>;

/**
 * Hush Nook cast as world and portrait render them (Hush wardrobe, batch-003).
 *
 * Pip wears a patched cap, a blue neck wrap with a short crooked ribbon, and holds the
 * paper clue boat on his palm — the roadside clue in Tidewend Outskirts.
 * Mallow wears a sea-glass/moss shawl with a low fold and a matte brass clasp, and holds
 * a smooth stone while teaching Lanternskip in the sheltered pocket refuge.
 */
export const HUSH_NOOK_CAST: Record<string, HumanoidAppearance> = {
  pip_reedwhistle: adaptWardrobeAppearance(HUSH_NOOK_BASE_CAST.pip_reedwhistle, HUSH_WARDROBE_PIP, HUSH_RENDER_TOKENS, HUSH_WARDROBE_RESOURCES),
  mallow_wick: adaptWardrobeAppearance(HUSH_NOOK_BASE_CAST.mallow_wick, HUSH_WARDROBE_MALLOW, HUSH_RENDER_TOKENS, HUSH_WARDROBE_RESOURCES),
};

const POSED = new Map<string, HumanoidAppearance>();
const WARDROBE = { pip_reedwhistle: HUSH_WARDROBE_PIP, mallow_wick: HUSH_WARDROBE_MALLOW } as const;

/**
 * Pip or Mallow in a talk/gesture pose (batch-003 variants; hideout plan P7). The world sprite stays idle; the
 * dialogue portrait asks for `talk` while the conversation is open. Returns undefined for anyone else.
 */
export function hushCastPosed(npcId: string | undefined, pose: 'idle' | 'talk' | 'gesture'): HumanoidAppearance | undefined {
  if (!npcId || !Object.prototype.hasOwnProperty.call(WARDROBE, npcId)) return undefined;
  const id = npcId as keyof typeof WARDROBE;
  if (pose === 'idle') return HUSH_NOOK_CAST[id];
  const key = id + ':' + pose;
  let posed = POSED.get(key);
  if (!posed) {
    const recipe = WARDROBE[id];
    posed = adaptWardrobeAppearance(HUSH_NOOK_BASE_CAST[id], { ...recipe, params: { ...recipe.params, pose } }, HUSH_RENDER_TOKENS, HUSH_WARDROBE_RESOURCES);
    POSED.set(key, posed);
  }
  return posed;
}
